import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  BlockOutlined,
  CancelOutlined,
  ContentCutOutlined,
  LocalPrintshopOutlined,
  PointOfSaleOutlined,
  RefreshOutlined,
  SearchOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import DatePick from "../../Components/DatePick";
import MainText from "../../Components/MainText";
import { printSaleTicket } from "../../Components/sale/SaleTicket";
import { APP_CONFIG } from "../../config/appConfig";
import { hasPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { groomingService, saleService } from "../../services";

const ORDER_STATUS_OPTIONS = [
  { value: "", label: "Todos los estados" },
  { value: "EN_PROCESO", label: "En proceso" },
  { value: "LISTO_ENTREGA", label: "Listo para cobrar" },
  { value: "COBRADO", label: "Cobrado" },
  { value: "CANCELADO", label: "Cancelado" },
];

const SEARCH_DEBOUNCE_MS = 350;
const GROOMING_TIME_ZONE = APP_CONFIG.timeZone;
const ORDERS_GRID_INITIAL_STATE = {
  pagination: {
    paginationModel: { page: 0, pageSize: 20 },
  },
};

const GRID_SX = {
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#757B2B",
    color: "#ffffff",
  },
  "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover": {
    backgroundColor: "#757B2B !important",
  },
  "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator":
  {
    color: "#ffffff !important",
  },
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("es-GT", {
    timeZone: APP_CONFIG.timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const toMoney = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "Q0.00";
  return `Q${parsed.toFixed(2)}`;
};

const toQueryDate = (value) => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return [
    String(date.getFullYear()),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const getTodayInGuatemala = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: GROOMING_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = formatter.formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value || "1970";
  const month = parts.find((part) => part.type === "month")?.value || "01";
  const day = parts.find((part) => part.type === "day")?.value || "01";
  return new Date(`${year}-${month}-${day}T00:00:00`);
};

const extractErrorMessage = (error, fallback = "No se pudo completar la operacion.") => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return fallback;
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];
  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  return fallback;
};

const getOrderStatusMeta = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "EN_PROCESO") return { label: "En proceso", color: "warning" };
  if (normalized === "LISTO_ENTREGA") return { label: "Listo para cobrar", color: "info" };
  if (normalized === "COBRADO") return { label: "Cobrado", color: "success" };
  if (normalized === "CANCELADO") return { label: "Cancelado", color: "error" };
  if (normalized === "DRAFT") return { label: "En proceso", color: "warning" };
  if (normalized === "RECEIVED") return { label: "Recibido", color: "info" };
  if (normalized === "IN_PROGRESS") return { label: "En proceso", color: "warning" };
  if (normalized === "READY") return { label: "Listo para cobrar", color: "info" };
  if (normalized === "DELIVERED") return { label: "Listo para cobrar", color: "info" };
  if (normalized === "CANCELED") return { label: "Cancelado", color: "error" };
  if (normalized === "PAUSED") return { label: "Pausado", color: "warning" };
  return { label: "En proceso", color: "default" };
};

const getPaymentStatusMeta = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (["PAID", "COMPLETED"].includes(normalized)) return { label: "Cobrado", color: "success" };
  if (normalized === "PARTIAL") return { label: "Parcial", color: "warning" };
  if (["VOID", "VOIDED", "CANCELED", "CANCELLED"].includes(normalized)) return { label: "Cancelado", color: "default" };
  return { label: "Pendiente", color: "default" };
};

const getSoldBy = (sale) =>
  sale?.sold_by_display ||
  sale?.sold_by_name ||
  sale?.sold_by?.name ||
  sale?.created_by_name ||
  sale?.created_by?.name ||
  "-";

const isPaidSale = (sale) =>
  ["COMPLETED", "RETURNED"].includes(String(sale?.status || "").toUpperCase());

const isVoidedOrCanceledSale = (sale) =>
  ["VOIDED", "CANCELED"].includes(String(sale?.status || "").toUpperCase());

const resolveOrderUiStatus = (row) => {
  const explicitUi = String(row?.ui_status || "").toUpperCase();
  if (["EN_PROCESO", "LISTO_ENTREGA", "COBRADO", "CANCELADO"].includes(explicitUi)) {
    return explicitUi;
  }
  if (isVoidedOrCanceledSale(row?.sale)) return "CANCELADO";
  if (String(row?.status || "").toUpperCase() === "CANCELED" || row?.canceled_at) return "CANCELADO";
  if (Boolean(row?.is_paid) || isPaidSale(row?.sale)) return "COBRADO";
  if (["READY", "DELIVERED"].includes(String(row?.status || "").toUpperCase())) return "LISTO_ENTREGA";
  return "EN_PROCESO";
};

function GroomingOrdersPage() {
  const navigate = useNavigate();
  const { currentBranch, effectivePerms = [], isSuperuser } = useAuth();
  const branchId = currentBranch?.id || null;
  const canViewPage = hasPermission(effectivePerms, "grooming_view", isSuperuser);
  const canManage = hasPermission(effectivePerms, "grooming_manage", isSuperuser);
  const canOpenDetail = canViewPage;
  const canPayNow = hasPermission(effectivePerms, "sale_pay_now", isSuperuser);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(() => getTodayInGuatemala());
  const [dateTo, setDateTo] = useState(() => getTodayInGuatemala());

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: "",
    row: null,
    reason: "",
    submitting: false,
  });

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const loadRows = useCallback(async () => {
    if (!branchId || !canViewPage) {
      setRows([]);
      return;
    }

    setLoading(true);
    try {
      const data = await groomingService.listOrders({
        branch_id: branchId,
        q: debouncedSearch || undefined,
        date_field: "created_at",
        date_from: toQueryDate(dateFrom),
        date_to: toQueryDate(dateTo),
      });
      const baseRows = toArray(data);
      const filteredRows = statusFilter
        ? baseRows.filter((row) => resolveOrderUiStatus(row) === statusFilter)
        : baseRows;
      setRows(filteredRows);
    } catch (error) {
      setRows([]);
      showSnack(extractErrorMessage(error, "No se pudieron cargar las ordenes grooming."), "error");
    } finally {
      setLoading(false);
    }
  }, [
    branchId,
    canViewPage,
    dateFrom,
    dateTo,
    debouncedSearch,
    showSnack,
    statusFilter,
  ]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleDateChange = useCallback((field, value) => {
    if (field === "date_from") setDateFrom(value);
    if (field === "date_to") setDateTo(value);
  }, []);

  const clearFilters = useCallback(() => {
    setSearch("");
    setStatusFilter("");
    setDateFrom(getTodayInGuatemala());
    setDateTo(getTodayInGuatemala());
  }, []);

  const handlePrintTicket = useCallback(
    async (row) => {
      if (!row?.sale?.id) return;
      if (!isPaidSale(row?.sale)) {
        showSnack("El ticket solo esta disponible despues del cobro confirmado.", "warning");
        return;
      }
      try {
        const sale = await saleService.getSale(row.sale.id, { branch_id: branchId });
        printSaleTicket({
          sale,
          branchName: sale?.branch_name || currentBranch?.name || "-",
          cashierName: getSoldBy(sale),
        });
        showSnack("Ticket enviado a impresion.", "success");
      } catch (error) {
        showSnack(extractErrorMessage(error, "No se pudo imprimir el ticket."), "error");
      }
    },
    [branchId, currentBranch?.name, showSnack]
  );

  const openActionDialog = useCallback((type, row) => {
    setActionDialog({
      open: true,
      type,
      row: row || null,
      reason: "",
      submitting: false,
    });
  }, []);

  const closeActionDialog = useCallback(() => {
    setActionDialog((prev) => {
      if (prev.submitting) return prev;
      return {
        open: false,
        type: "",
        row: null,
        reason: "",
        submitting: false,
      };
    });
  }, []);

  const handleConfirmActionDialog = useCallback(async () => {
    const row = actionDialog.row;
    if (!row?.id || !branchId || !actionDialog.type) return;

    const reason = String(actionDialog.reason || "").trim();
    if (!reason) {
      showSnack(
        actionDialog.type === "cancel-order"
          ? "Debes ingresar la razon de cancelacion."
          : "Debes ingresar la razon de anulacion.",
        "warning"
      );
      return;
    }

    setActionDialog((prev) => ({ ...prev, submitting: true }));
    try {
      if (actionDialog.type === "cancel-order") {
        await groomingService.cancelOrder(row.id, {
          branch_id: branchId,
          reason,
          notes: reason,
        });
        showSnack("Orden grooming cancelada correctamente.", "success");
      } else if (actionDialog.type === "void-sale") {
        if (!row?.sale?.id) {
          showSnack("No hay venta relacionada para anular.", "warning");
          setActionDialog((prev) => ({ ...prev, submitting: false }));
          return;
        }
        await saleService.voidSale(row.sale.id, {
          branch_id: branchId,
          reason,
        });
        showSnack("Venta grooming anulada correctamente.", "success");
      }

      setActionDialog({
        open: false,
        type: "",
        row: null,
        reason: "",
        submitting: false,
      });
      await loadRows();
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo completar la accion."), "error");
      setActionDialog((prev) => ({ ...prev, submitting: false }));
    }
  }, [actionDialog.reason, actionDialog.row, actionDialog.type, branchId, loadRows, showSnack]);

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "Orden",
        minWidth: 100,
        flex: 0.5,
        valueGetter: (_, row) => `#${row?.id || "-"}`,
      },
      {
        field: "pet",
        headerName: "Mascota",
        minWidth: 180,
        flex: 1,
        valueGetter: (_, row) => row?.pet?.name || "-",
      },
      {
        field: "customer",
        headerName: "Cliente",
        minWidth: 190,
        flex: 1,
        valueGetter: (_, row) => row?.customer?.name || "-",
      },
      {
        field: "assigned_to",
        headerName: "Asignado",
        minWidth: 170,
        flex: 0.9,
        valueGetter: (_, row) => row?.assigned_to?.name || "-",
      },
      {
        field: "status",
        headerName: "Estado",
        minWidth: 150,
        flex: 0.8,
        renderCell: ({ row }) => {
          const meta = getOrderStatusMeta(resolveOrderUiStatus(row));
          return <Chip size="small" variant="outlined" label={meta.label} color={meta.color} />;
        },
      },
      {
        field: "payment_status",
        headerName: "Cobro",
        minWidth: 140,
        flex: 0.75,
        renderCell: ({ row }) => {
          const meta = getPaymentStatusMeta(row?.payment_status);
          return <Chip size="small" variant="outlined" label={meta.label} color={meta.color} />;
        },
      },
      {
        field: "total",
        headerName: "Total",
        minWidth: 120,
        flex: 0.6,
        valueGetter: (_, row) => toMoney(row?.total),
      },
      {
        field: "sale",
        headerName: "Venta",
        minWidth: 180,
        flex: 0.9,
        renderCell: ({ row }) => {
          const isPaid = Boolean(row?.is_paid) || isPaidSale(row?.sale);
          if (!row?.sale?.id) {
            return <Chip size="small" variant="outlined" label="Sin venta" />;
          }
          return (
            <Stack spacing={0.25}>
              <Chip
                size="small"
                color={isPaid ? "success" : "warning"}
                label={row.sale.sale_number || `Venta #${row.sale.id}`}
              />
            </Stack>
          );
        },
      },
      {
        field: "created_at",
        headerName: "Creada",
        minWidth: 190,
        flex: 0.9,
        valueGetter: (_, row) => formatDateTime(row?.created_at),
      },
      {
        field: "actions",
        headerName: "Acciones",
        minWidth: 360,
        flex: 1.5,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const uiStatus = resolveOrderUiStatus(row);
          const isPaid = uiStatus === "COBRADO";
          const canEditDetail = canManage && ["EN_PROCESO", "LISTO_ENTREGA"].includes(uiStatus);
          const canCancelOrder = canManage && ["EN_PROCESO", "LISTO_ENTREGA"].includes(uiStatus);
          const canVoidSale = isSuperuser && uiStatus === "COBRADO" && Boolean(row?.sale?.id);
          const canShowTicket = isPaidSale(row?.sale);
          const canCharge =
            uiStatus === "LISTO_ENTREGA" &&
            !isPaid &&
            canPayNow;

          return (
            <Stack direction="row" spacing={0.75} sx={{ py: 0.5 }} useFlexGap flexWrap="wrap">
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityOutlined />}
                onClick={() => navigate(`/grooming/orders/${row.id}`)}
                disabled={!canOpenDetail}
              >
                Ver detalle
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => navigate(`/grooming/orders/${row.id}`)}
                disabled={!canEditDetail}
              >
                Editar detalle
              </Button>
              {canCancelOrder ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<CancelOutlined />}
                  onClick={() => openActionDialog("cancel-order", row)}
                >
                  Cancelar
                </Button>
              ) : null}
              {canVoidSale ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<BlockOutlined />}
                  onClick={() => openActionDialog("void-sale", row)}
                >
                  Anular venta
                </Button>
              ) : null}
              {canShowTicket ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<LocalPrintshopOutlined />}
                  onClick={() => handlePrintTicket(row)}
                >
                  Ver ticket
                </Button>
              ) : canCharge ? (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PointOfSaleOutlined />}
                  onClick={() => navigate(`/grooming/orders/${row.id}`)}
                >
                  Cobrar
                </Button>
              ) : null}
            </Stack>
          );
        },
      },
    ],
    [canManage, canOpenDetail, canPayNow, handlePrintTicket, isSuperuser, navigate, openActionDialog]
  );

  const readyForDeliveryRows = useMemo(
    () => rows.filter((row) => resolveOrderUiStatus(row) === "LISTO_ENTREGA"),
    [rows]
  );

  if (!canViewPage) {
    return <Alert severity="warning">No tienes permisos para ver grooming.</Alert>;
  }

  if (!branchId) {
    return <Alert severity="info">Selecciona una sucursal para operar grooming.</Alert>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <MainText
        title="Ordenes grooming"
        subtitle="Seguimiento operativo y comercial por sucursal"
        icon={ContentCutOutlined}
      />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box sx={{ fontWeight: 700 }}>Listos para cobrar</Box>
            <Chip size="small" variant="outlined" label={`${readyForDeliveryRows.length} orden(es)`} />
          </Stack>
          {readyForDeliveryRows.length === 0 ? (
            <Box sx={{ color: "text.secondary", fontSize: 13 }}>
              No hay ordenes listas para cobrar en este momento.
            </Box>
          ) : (
            readyForDeliveryRows.map((row) => (
              <Paper key={row.id} variant="outlined" sx={{ p: 1.2 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between">
                  <Stack spacing={0.3}>
                    <Box sx={{ fontWeight: 600 }}>{row?.pet?.name || "-"} · {row?.customer?.name || "-"}</Box>
                    <Box sx={{ color: "text.secondary", fontSize: 13 }}>Total: {toMoney(row?.total)}</Box>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PointOfSaleOutlined />}
                      onClick={() => navigate(`/grooming/orders/${row.id}`)}
                      disabled={!canPayNow}
                    >
                      Cobrar
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityOutlined />}
                      onClick={() => navigate(`/grooming/orders/${row.id}`)}
                      disabled={!canOpenDetail}
                    >
                      Detalle
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            ))
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} useFlexGap flexWrap="wrap">
            <TextField
              label="Buscar"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Orden, cliente, telefono o mascota"
              size="small"
              sx={{ minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Estado"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
            >
              {ORDER_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value || "all"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <DatePick
              name="date_from"
              label="Fecha inicial"
              value={dateFrom}
              onChange={handleDateChange}
              size="small"
              allowHistoricalDates
              maxWidth={220}
              minWidth={180}
              width="220px"
            />
            <DatePick
              name="date_to"
              label="Fecha final"
              value={dateTo}
              onChange={handleDateChange}
              size="small"
              allowHistoricalDates
              maxWidth={220}
              minWidth={180}
              width="220px"
            />
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={loadRows}>
              Actualizar
            </Button>
            <Button variant="text" onClick={clearFilters}>
              Limpiar filtros
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ height: "calc(100vh - 290px)", minHeight: 420 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          pageSizeOptions={[10, 20, 50]}
          initialState={ORDERS_GRID_INITIAL_STATE}
          sx={GRID_SX}
        />
      </Paper>

      <Dialog open={actionDialog.open} onClose={closeActionDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {actionDialog.type === "cancel-order" ? "Cancelar orden grooming" : "Anular venta grooming"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.2}>
            <Box sx={{ color: "text.secondary", fontSize: 14 }}>
              {actionDialog.type === "cancel-order"
                ? "Debes registrar una razon obligatoria para cancelar la orden."
                : "Debes registrar una razon obligatoria para anular la venta cobrada."}
            </Box>
            <TextField
              label={
                actionDialog.type === "cancel-order"
                  ? "Razon de cancelacion"
                  : "Razon de anulacion"
              }
              value={actionDialog.reason}
              onChange={(event) =>
                setActionDialog((prev) => ({ ...prev, reason: event.target.value }))
              }
              multiline
              minRows={3}
              fullWidth
              disabled={actionDialog.submitting}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog} disabled={actionDialog.submitting}>
            Cerrar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmActionDialog}
            disabled={actionDialog.submitting}
          >
            {actionDialog.submitting ? "Procesando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}

export default GroomingOrdersPage;
